import { freshCardInstances, type Composition, type PrecursorTier, type StageDef } from "@reagent/sim";
import { ALL_CARDS } from "./cards";
import type { EraPoolDef, PathwayDef } from "./pathways";
import { validateStage } from "./schema";
import type { LiveBuildStep, StagePlay, StageSolution } from "./solutions";

/**
 * Composing a run's stages out of pathway line modules (spec 0034).
 *
 * A module is a complete, validated stage. Composition stacks modules
 * top-to-bottom on one board and adds nothing of its own: every number in the
 * result is either a module's number or a sum of them, so a composed stage
 * invents no balance.
 */

/** The schema's board cap; `stageSchema` enforces it too, but only after the fact and without naming the modules. */
const MAX_COLS = 16;
const MAX_ROWS = 10;

/** The engine's minted node ids. A composed solution has to renumber these, because one engine mints them in a single global sequence. */
const MINTED_NODE_ID = /^n(\d+)$/;

/** The engine's minted edge ids, which a composed solution cannot renumber (edges are named by endpoints elsewhere). */
const MINTED_EDGE_ID = /^e\d+$/;

/** The per-stage scalars a composed stage cannot sum without making a balance decision (OQ3): they must already agree. */
const SHARED_SCALARS = [
  ["ticksPerTurn", (s: StageDef) => s.ticksPerTurn],
  ["maxTurns", (s: StageDef) => s.maxTurns],
  ["energyBudgetKw", (s: StageDef) => s.energyBudgetKw],
  ["handSize", (s: StageDef) => s.handSize],
  ["objective.minPurity", (s: StageDef) => s.objective.minPurity],
  // Spec 0067: the live-build rule is one object, compared as canonical data (absent on every pre-0067 stage).
  ["liveBuild", (s: StageDef) => canonical(s.liveBuild)],
] as const;

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record)
      .filter((k) => record[k] !== undefined)
      .sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(record[k])}`).join(",")}}`;
  }
  return JSON.stringify(value) ?? "undefined";
}

/** Deep equality for plain data, insensitive to key order and to `undefined` fields — recipes are authored both ways. */
export function sameData(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

/**
 * Where the shared-scalar rule is enforced, so `validatePool` and composition
 * report the same violation the same way. Returns the first offending scalar.
 */
export function scalarMismatch(
  modules: readonly StageDef[],
): { readonly key: string; readonly values: readonly { readonly id: string; readonly value: number | string }[] } | null {
  for (const [key, read] of SHARED_SCALARS) {
    const values = modules.map((m) => ({ id: m.id, value: read(m) }));
    if (values.some((v) => v.value !== values[0]!.value)) return { key, values };
  }
  return null;
}

/** Stacks modules top to bottom in order; see spec 0034 Constraints "Composition". Returns a validateStage'd StageDef. */
export function composeLineModules(id: string, name: string, modules: readonly StageDef[]): StageDef {
  if (modules.length === 0) throw new Error(`composeLineModules ${id}: no modules to compose`);

  const mismatch = scalarMismatch(modules);
  if (mismatch) {
    const detail = mismatch.values.map((v) => `${v.id}=${v.value}`).join(", ");
    throw new Error(`composeLineModules ${id}: ${mismatch.key} differs across modules (${detail})`);
  }

  const cols = Math.max(...modules.map((m) => m.cols));
  const rows = modules.reduce((sum, m) => sum + m.rows, 0);
  const names = modules.map((m) => m.id).join(", ");
  if (cols > MAX_COLS || rows > MAX_ROWS) {
    throw new Error(`composeLineModules ${id}: ${cols}x${rows} exceeds the ${MAX_COLS}x${MAX_ROWS} board (modules: ${names})`);
  }

  const nodes: StageDef["nodes"][number][] = [];
  const edges: StageDef["edges"][number][] = [];
  const recipes = new Map<string, StageDef["recipes"][number]>();
  const pollutionCards: Record<string, string> = {};
  const corrosive: string[] = [];
  const events: NonNullable<StageDef["scriptedEvents"]>[number][] = [];
  const catalogue = new Map<string, NonNullable<StageDef["setupCatalogue"]>[number]>();
  const target: Record<string, number> = {};
  const deckIds: string[] = [];
  let hasSetup = false;
  let setupBudget = 0;
  let rowsAbove = 0;

  for (const module of modules) {
    for (const node of module.nodes) nodes.push({ ...node, pos: { x: node.pos.x, y: node.pos.y + rowsAbove } });
    for (const edge of module.edges) {
      edges.push({ ...edge, path: edge.path.map((p) => ({ x: p.x, y: p.y + rowsAbove })) });
    }

    for (const recipe of module.recipes) {
      const seen = recipes.get(recipe.id);
      if (seen && !sameData(seen, recipe)) {
        throw new Error(`composeLineModules ${id}: recipe ${recipe.id} differs between modules (${names})`);
      }
      recipes.set(recipe.id, recipe);
    }

    for (const [reason, cardId] of Object.entries(module.pollutionCards ?? {})) {
      if (cardId === undefined) continue;
      if (pollutionCards[reason] !== undefined && pollutionCards[reason] !== cardId) {
        throw new Error(`composeLineModules ${id}: pollutionCards ${reason} maps to both ${pollutionCards[reason]} and ${cardId} (${module.id})`);
      }
      pollutionCards[reason] = cardId;
    }

    for (const species of module.corrosiveSpecies) if (!corrosive.includes(species)) corrosive.push(species);
    events.push(...(module.scriptedEvents ?? []));

    for (const offer of module.setupCatalogue ?? []) {
      const seen = catalogue.get(offer.cardId);
      if (seen && seen.costCredits !== offer.costCredits) {
        throw new Error(`composeLineModules ${id}: ${offer.cardId} is offered at ${seen.costCredits} and ${offer.costCredits} (${module.id})`);
      }
      catalogue.set(offer.cardId, offer);
    }
    if (module.setupBudgetCredits !== undefined || module.setupCatalogue !== undefined) hasSetup = true;
    setupBudget += module.setupBudgetCredits ?? 0;

    for (const [species, amount] of Object.entries(module.objective.targetComposition)) {
      target[species] = (target[species] ?? 0) + amount;
    }
    deckIds.push(...module.startingDeck.map((ci) => ci.cardId));
    rowsAbove += module.rows;
  }

  const first = modules[0]!;
  const stage: StageDef = {
    id,
    name,
    blurb: modules.map((m) => m.name).join(" + "),
    cols,
    rows,
    nodes,
    edges,
    recipes: [...recipes.values()],
    objective: { targetComposition: target, minPurity: first.objective.minPurity },
    energyBudgetKw: first.energyBudgetKw,
    handSize: first.handSize,
    ticksPerTurn: first.ticksPerTurn,
    maxTurns: first.maxTurns,
    ...(first.liveBuild !== undefined ? { liveBuild: first.liveBuild } : {}),
    corrosiveSpecies: corrosive,
    startingDeck: freshCardInstances(deckIds),
    pollutionCards,
    ...(events.length > 0 ? { scriptedEvents: events } : {}),
    ...(hasSetup ? { setupBudgetCredits: setupBudget, setupCatalogue: [...catalogue.values()] } : {}),
  };
  return validateStage(stage, ALL_CARDS);
}

/**
 * The same layout transform applied to the modules' worked solutions: tile
 * offsets, and the engine's minted node ids renumbered.
 *
 * The engine mints `n1, n2, ...` in one sequence per stage, so a part's `n1`
 * is the composed stage's `n(1 + installs in the parts above it)`. That holds
 * for Setup builds only: a Play-phase hardware card would mint in turn order
 * across parts, which this transform cannot reorder, so it refuses one rather
 * than emit a solution that quietly fails.
 */
export function composeLineSolutions(
  id: string,
  parts: readonly { readonly stage: StageDef; readonly solution: StageSolution }[],
): StageSolution {
  const cardsById = new Map(ALL_CARDS.map((c) => [c.id, c]));
  const mintsNode = (play: StagePlay): boolean => cardsById.get(play.cardId)?.deploysNode !== undefined;

  const setupBuild: StagePlay[] = [];
  const turns: StagePlay[][] = [];
  const liveSetup: LiveBuildStep[] = [];
  const liveTurns: LiveBuildStep[][] = [];
  const notes: string[] = [];
  const sourcing: Record<string, PrecursorTier> = {};
  let rowsAbove = 0;
  let mintedAbove = 0;

  for (const { stage, solution } of parts) {
    const renumber = (nodeId: string | undefined): string | undefined => {
      const minted = nodeId === undefined ? null : MINTED_NODE_ID.exec(nodeId);
      return minted ? `n${Number(minted[1]) + mintedAbove}` : nodeId;
    };
    const place = (play: StagePlay): StagePlay => ({
      cardId: play.cardId,
      ...(play.at ? { at: { x: play.at.x, y: play.at.y + rowsAbove } } : {}),
      ...(play.fromNodeId !== undefined ? { fromNodeId: renumber(play.fromNodeId)! } : {}),
      ...(play.toNodeId !== undefined ? { toNodeId: renumber(play.toNodeId)! } : {}),
    });

    // Spec 0067 live-build steps: same tile offset and node renumbering. Minted
    // edge ids and Setup-time minting installs cannot be reordered across parts
    // (the engine mints in one global sequence), so those are refused like a Play deploy.
    const placeStep = (step: LiveBuildStep): LiveBuildStep => {
      if (step.build === "clean") return { build: "clean", at: { x: step.at.x, y: step.at.y + rowsAbove } };
      if (step.build === "install") {
        return {
          build: "install",
          cardId: step.cardId,
          ...(step.at ? { at: { x: step.at.x, y: step.at.y + rowsAbove } } : {}),
          ...(step.fromNodeId !== undefined ? { fromNodeId: renumber(step.fromNodeId)! } : {}),
          ...(step.toNodeId !== undefined ? { toNodeId: renumber(step.toNodeId)! } : {}),
        };
      }
      if (step.edgeId !== undefined && MINTED_EDGE_ID.test(step.edgeId)) {
        throw new Error(`composeLineSolutions ${id}: ${stage.id} names minted edge ${step.edgeId} in a live-build step; edge ids cannot be renumbered`);
      }
      return {
        build: step.build,
        ...(step.nodeId !== undefined ? { nodeId: renumber(step.nodeId)! } : {}),
        ...(step.edgeId !== undefined ? { edgeId: step.edgeId } : {}),
        ...(step.build === "move"
          ? {
              ...(step.at ? { at: { x: step.at.x, y: step.at.y + rowsAbove } } : {}),
              ...(step.fromNodeId !== undefined ? { fromNodeId: renumber(step.fromNodeId)! } : {}),
              ...(step.toNodeId !== undefined ? { toNodeId: renumber(step.toNodeId)! } : {}),
            }
          : {}),
      } as LiveBuildStep;
    };
    const stepMints = (step: LiveBuildStep): boolean =>
      step.build === "install" && cardsById.get(step.cardId)?.deploysNode !== undefined;

    for (const step of solution.liveBuild?.setup ?? []) {
      if (stepMints(step)) throw new Error(`composeLineSolutions ${id}: ${stage.id} installs a node in a Setup live-build step; only setupBuild can be renumbered`);
      liveSetup.push(placeStep(step));
    }
    for (const [index, steps] of (solution.liveBuild?.turns ?? []).entries()) {
      if (steps.some(stepMints)) {
        throw new Error(`composeLineSolutions ${id}: ${stage.id} turn ${index + 1} installs a node in play; only Setup builds can be renumbered`);
      }
      (liveTurns[index] ??= []).push(...steps.map(placeStep));
    }

    const build = solution.setupBuild ?? [];
    setupBuild.push(...build.map(place));

    for (const [index, plays] of solution.turns.entries()) {
      if (plays.some(mintsNode)) {
        throw new Error(`composeLineSolutions ${id}: ${stage.id} turn ${index + 1} deploys a node in play; only Setup builds can be renumbered`);
      }
      (turns[index] ??= []).push(...plays.map(place));
    }

    // Node ids are unique across a pool (`validatePool`), so contracts union without renaming.
    Object.assign(sourcing, solution.sourcing);
    notes.push(solution.notes);
    rowsAbove += stage.rows;
    mintedAbove += build.filter(mintsNode).length;
  }

  // A turn no part plays in would leave a hole in the array, which `for..of` would visit as undefined.
  for (let i = 0; i < turns.length; i++) turns[i] ??= [];
  for (let i = 0; i < liveTurns.length; i++) liveTurns[i] ??= [];

  return {
    stageId: id,
    ...(Object.keys(sourcing).length > 0 ? { sourcing } : {}),
    ...(setupBuild.length > 0 ? { setupBuild } : {}),
    turns,
    ...(liveSetup.length > 0 || liveTurns.some((t) => t.length > 0)
      ? { liveBuild: { ...(liveSetup.length > 0 ? { setup: liveSetup } : {}), ...(liveTurns.length > 0 ? { turns: liveTurns } : {}) } }
      : {}),
    notes: notes.join(" "),
  };
}

function pathwayFor(pool: EraPoolDef, pathwayId: string): PathwayDef {
  const found = pool.pathways.find((p) => p.id === pathwayId);
  if (!found) throw new Error(`pairingStages: unknown pathway ${pathwayId} in pool ${pool.eraId}`);
  return found;
}

/**
 * The worst impurity a partner can hand over: per species, the largest Budget
 * impurity any sourced tank on its core line carries. It bounds what could ride
 * into the export whatever the routing, and is never empty because a sourced
 * tank's Budget impurities are not (`sourcingDef`). Spec 0037 OQ1.
 */
export function worstCaseExport(exporter: PathwayDef): Composition {
  const worst: Record<string, number> = {};
  for (const node of exporter.core.module.nodes) {
    if (node.kind !== "inputTank" || node.sourcing === undefined) continue;
    for (const [name, units] of Object.entries(node.sourcing.budget.impurities)) {
      worst[name] = Math.max(worst[name] ?? 0, units);
    }
  }
  return worst;
}

/** The impurities a linked tank for `token` carries: the exporter's worst case plus its authored substitute grade. */
export function linkedImpurities(exporter: PathwayDef, token: string): Composition {
  const merged: Record<string, number> = { ...worstCaseExport(exporter) };
  for (const [name, units] of Object.entries(exporter.substituteImpurities?.[token] ?? {})) {
    merged[name] = (merged[name] ?? 0) + units;
  }
  return merged;
}

/**
 * PLACEHOLDER (spec 0037, needs playtest): an internal feed is "cheapest; paid
 * once" (`06` section 2), so the partner contract costs half the external Budget price.
 */
function partnerCost(budgetCost: number): number {
  return Math.floor(budgetCost / 2);
}

/**
 * Replaces the Budget contract of every sourced import tank in `module` whose
 * token `exporter` exports with the partner contract (OQ1 (a)). Premium stays
 * external and clean, so the player can still buy their way out of a dirty link.
 */
export function linkModule(module: StageDef, exporter: PathwayDef): StageDef {
  const nodes = module.nodes.map((node) => {
    if (node.kind !== "inputTank" || node.sourcing === undefined || node.emits === undefined) return node;
    const emitted = Object.keys(node.emits);
    const token = emitted.length === 1 ? emitted[0]! : undefined;
    if (token === undefined || !exporter.exports.includes(token)) return node;
    return {
      ...node,
      sourcing: {
        ...node.sourcing,
        budget: { impurities: linkedImpurities(exporter, token), costCredits: partnerCost(node.sourcing.budget.costCredits) },
      },
    };
  });
  return { ...module, nodes };
}

/**
 * The run's three stages for an ordered pick: the first pathway's core alone,
 * both cores, then both refined tiers (spec 0034 OQ2 — the cores rejoin with
 * plant carry, not before). The placeholder links (spec 0037 OQ1) apply wherever
 * both partners are on the board: C imports from stage 2, R imports at stage 3.
 */
export function pairingStages(
  pool: EraPoolDef,
  firstId: string,
  secondId: string,
): readonly [StageDef, StageDef, StageDef] {
  if (firstId === secondId) throw new Error(`pairingStages: a run needs two different pathways, got ${firstId} twice`);
  const a = pathwayFor(pool, firstId);
  const b = pathwayFor(pool, secondId);
  const label = `${a.name} + ${b.name}`;
  return [
    a.core.module,
    composeLineModules(`${a.id}-${b.id}-cores`, label, [linkModule(a.core.module, b), linkModule(b.core.module, a)]),
    composeLineModules(`${a.id}-${b.id}-refined`, `${label} (refined)`, [
      linkModule(a.refined.module, b),
      linkModule(b.refined.module, a),
    ]),
  ];
}
